// Fenêtre du challenge REPAS — forcée au 14–25 septembre 2026.
// Source de vérité unique : le sélecteur de dates et le libellé « semaine »
// de l'écran « Tes actions » s'appuient dessus (au lieu de la semaine en cours).
//
// Les jours sont représentés en chaînes 'YYYY-MM-DD' (comme partout dans
// l'app, cf. utils/date.ts) et JAMAIS en objets Date : un Date « minuit
// local » dérive d'un jour selon le fuseau de l'appareil (un utilisateur en
// UTC+3 sélectionnant le 17 interrogeait le 16 en base).

import { getTodayCH, shiftDayString } from '@/utils/date';

// Fenêtre RÉELLE du concours — toujours affichée telle quelle (demande client).
// 14–25 septembre 2026 = 12 jours (lun. 14 → ven. 25), décision client 2026-07-13.
export const CHALLENGE_START = '2026-09-14';
export const CHALLENGE_END = '2026-09-25';

// ── MODE TEST (ADMINS UNIQUEMENT) ───────────────────────────────────────────
// Le serveur force `action_date = jour réel` (trigger d'intégrité) : on ne peut
// pas mentir sur la date côté client. Pour tester la « validation le jour même »
// AVANT septembre tout en AFFICHANT les vraies dates du concours, on mappe le
// jour réel sur le jour du concours de MÊME jour de semaine (écart multiple de
// 7 → jours de semaine alignés). L'affichage reste « 14–25 septembre ».
//
// La simulation n'est active QUE pour les comptes admin (utils/admin.ts) :
// app/_layout.tsx appelle setChallengeSimulation(isAdminEmail(...)) à chaque
// changement de session. Prod-safe PAR CONSTRUCTION : un utilisateur normal a
// toujours offset = 0 (vraies dates), aucun flag à penser à désactiver avant
// une release.
let simulationEnabled = false;

/** Active/désactive la simulation de la semaine de concours (admins QA).
 * Appelé par app/_layout.tsx au fil des changements de session. */
export function setChallengeSimulation(enabled: boolean): void {
  simulationEnabled = enabled;
}

function weekdayOf(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

// Décalage (jours) entre le jour réel et le jour du concours de même jour de
// semaine. Multiple de 7 par construction → alignement des jours de semaine.
// Recalculé à chaque appel (trivial) : le flag peut changer en cours de vie.
function currentOffsetDays(): number {
  if (!simulationEnabled) return 0;
  const today = getTodayCH();
  const dow = weekdayOf(today);
  let cursor = CHALLENGE_START;
  while (cursor <= CHALLENGE_END && weekdayOf(cursor) !== dow) {
    cursor = shiftDayString(cursor, 1);
  }
  const [ty, tm, td] = today.split('-').map(Number);
  const [cy, cm, cd] = cursor.split('-').map(Number);
  return Math.round(
    (Date.UTC(cy, cm - 1, cd) - Date.UTC(ty, tm - 1, td)) / 86400000
  );
}

/** « Aujourd'hui » exprimé dans le calendrier AFFICHÉ (concours).
 * Utilisateur normal (ou hors simulation) = jour réel. */
export function getDisplayToday(): string {
  return shiftDayString(getTodayCH(), currentOffsetDays());
}

/** Convertit un jour AFFICHÉ (concours) en jour RÉEL, pour les requêtes/écritures
 * en base (le serveur stocke la vraie date). Hors simulation = identité. */
export function toRealDate(displayDate: string): string {
  return shiftDayString(displayDate, -currentOffsetDays());
}

const MONTHS_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

/** Tous les jours du challenge (bornes incluses), pour la liste déroulante. */
export function getChallengeDays(): string[] {
  const days: string[] = [];
  let cursor = CHALLENGE_START;
  while (cursor <= CHALLENGE_END) {
    days.push(cursor);
    cursor = shiftDayString(cursor, 1);
  }
  return days;
}

/** Ramène un jour dans la fenêtre du challenge (avant → début, après → fin).
 * Comparaison lexicographique : valide pour des chaînes ISO 'YYYY-MM-DD'. */
export function clampToChallenge(day: string): string {
  if (day < CHALLENGE_START) return CHALLENGE_START;
  if (day > CHALLENGE_END) return CHALLENGE_END;
  return day;
}

/** Libellé d'un jour, ex. « Jeu. 17 septembre ». Calcul du jour de semaine en
 * UTC à partir des composantes — aucun fuseau appareil impliqué. */
export function formatChallengeDayFR(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const dayName = DAYS_FR[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${dayName}. ${d} ${MONTHS_FR[m - 1]}`;
}

/** La roue (onboarding + quotidienne) n'ouvre que le jour réel du challenge. */
export function isLuckyWheelOpenOn(day: string): boolean {
  return day >= CHALLENGE_START;
}

export function isLuckyWheelOpen(): boolean {
  return isLuckyWheelOpenOn(getTodayCH());
}

/** Minuit du 14 septembre 2026 à Genève (CEST = UTC+2 en septembre). */
export const CHALLENGE_START_AT_MS = Date.parse(`${CHALLENGE_START}T00:00:00+02:00`);

export function msUntilChallengeStart(now = Date.now()): number {
  return Math.max(0, CHALLENGE_START_AT_MS - now);
}

export function isChallengeStarted(now = Date.now()): boolean {
  return now >= CHALLENGE_START_AT_MS;
}

/** Libellé de la plage, ex. « Du 14 au 25 septembre ». */
export function getChallengeRangeLabel(): string {
  const [, startMonth, startDay] = CHALLENGE_START.split('-').map(Number);
  const [, endMonth, endDay] = CHALLENGE_END.split('-').map(Number);
  return startMonth === endMonth
    ? `Du ${startDay} au ${endDay} ${MONTHS_FR[endMonth - 1]}`
    : `Du ${startDay} ${MONTHS_FR[startMonth - 1]} au ${endDay} ${MONTHS_FR[endMonth - 1]}`;
}
